// Credit control for one client (staff): current hold status, a manual hold switch, and an optional
// credit limit. On hold = a payment 45+ days overdue (the final notice) or a manual hold; the client
// then can't place orders, and approving a rep/staff order needs an override. See creditService.
import { useEffect, useState } from 'react';
import { setCompanyCredit } from '../api/companies.js';
import { fmtMoney } from '../lib/matrix.js';

const CURRENCIES = ['AUD', 'USD', 'NZD', 'GBP', 'EUR', 'CAD'];
const errMsg = (e) => e.response?.data?.error?.message || 'Could not save.';

export default function CreditControlCard({ companyId, credit, canEdit, onSaved }) {
  const s = credit?.settings || {};
  const [hold, setHold] = useState(Boolean(s.creditHold));
  const [note, setNote] = useState(s.creditHoldNote || '');
  const [limit, setLimit] = useState(s.creditLimit != null ? String(s.creditLimit) : '');
  const [currency, setCurrency] = useState(s.creditLimitCurrency || 'AUD');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setHold(Boolean(s.creditHold));
    setNote(s.creditHoldNote || '');
    setLimit(s.creditLimit != null ? String(s.creditLimit) : '');
    setCurrency(s.creditLimitCurrency || 'AUD');
  }, [s.creditHold, s.creditHoldNote, s.creditLimit, s.creditLimitCurrency]);

  if (!credit) return null;

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const n = limit.trim() === '' ? null : Number(limit);
      if (n !== null && !(n > 0)) throw { response: { data: { error: { message: 'Credit limit must be a positive amount (or blank for none).' } } } };
      await setCompanyCredit({ companyId, creditHold: hold, creditHoldNote: note.trim() || null, creditLimit: n, creditLimitCurrency: currency });
      onSaved?.();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const status = credit.onHold ? { label: 'On hold', cls: 'bg-error-light text-error' } : credit.overLimit ? { label: 'Over limit', cls: 'bg-warning-light text-warning' } : { label: 'Good standing', cls: 'bg-success-light text-success' };

  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Credit</h3>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.cls}`}>{status.label}</span>
      </div>
      {credit.reasons?.length > 0 && (
        <ul className="mb-2 list-disc space-y-0.5 pl-4 text-xs text-text-secondary">
          {credit.reasons.map((r) => (
            <li key={r.code}>{r.message}</li>
          ))}
        </ul>
      )}
      {credit.limit && credit.exposure && (
        <p className="mb-2 text-xs text-text-muted">
          Owed on approved orders: {fmtMoney(credit.exposure.amount, credit.exposure.currency)} of {fmtMoney(credit.limit.amount, credit.limit.currency)} limit
        </p>
      )}
      {canEdit ? (
        <>
          <label className="flex items-center gap-2 text-xs text-text-secondary">
            <input type="checkbox" checked={hold} onChange={(e) => setHold(e.target.checked)} className="accent-accent" />
            Put this account on hold manually
          </label>
          {hold && (
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={300}
              placeholder="Reason (internal)"
              aria-label="Hold reason"
              className="mt-1 w-full rounded-md border border-line bg-surface px-2 py-1 text-sm text-text-primary"
            />
          )}
          <label className="mt-2 block text-xs text-text-muted">Credit limit (blank = none)</label>
          <div className="mt-0.5 flex gap-1.5">
            <input
              type="number"
              min={0}
              step="0.01"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              placeholder="No limit"
              aria-label="Credit limit"
              className="w-full rounded-md border border-line bg-surface px-2 py-1 text-sm text-text-primary"
            />
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} aria-label="Credit limit currency" className="rounded-md border border-line bg-surface px-2 py-1 text-sm text-text-primary">
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <button onClick={save} disabled={busy} className="mt-2 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50">
            {busy ? 'Saving…' : 'Save credit settings'}
          </button>
          {error && <p className="mt-1 text-xs text-error">{error}</p>}
          <p className="mt-1 text-[11px] text-text-muted">
            On hold: the client can’t place orders, and approving a rep/staff order needs an override. Payments 45+ days overdue put the account on hold automatically.
          </p>
        </>
      ) : (
        !credit.reasons?.length && <p className="text-xs text-text-muted">No holds.</p>
      )}
    </div>
  );
}
